import { FC, useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Direction } from '@/api';
import { AnalyzeItemScoreIndicator } from './components/AnalyzeItemScoreIndicator/AnalyzeItemScoreIndicator';

import styles from './AnalyzeItem.module.sass';

interface AnalyzeItemProps {
  type?: Direction;
  title?: string;
  description?: string;
  value?: string;
  caption?: string;
  isBlank?: boolean;
  hasOffset?: boolean;
  clusterAddress?: string;
  isMuted?: boolean;
  isSelected?: boolean;
  riskValue?: number;
  rawValue?: number;
  shareTotal?: number;
  path?: any[];
  className?: string;
  onClick?: () => void;
}

const AnalyzeItem: FC<AnalyzeItemProps> = ({
  type,
  title = '',
  description = '',
  value = '',
  caption = '',
  isBlank = false,
  hasOffset = false,
  clusterAddress = '',
  isMuted = false,
  isSelected = false,
  riskValue = null,
  rawValue,
  shareTotal,
  path = [],
  className,
  onClick = () => {},
}) => {
  const { t } = useTranslation();
  const isNegative = type === 'outcome';
  const amountSign = isNegative ? '-' : '+';
  const riskScore = useMemo(() => {
    if (riskValue === null || riskValue === undefined) return null;
    return (riskValue * 100).toFixed();
  }, [riskValue]);

  const sharePercentLabel = useMemo(() => {
    if (
      rawValue == null ||
      shareTotal == null ||
      shareTotal <= 0 ||
      Number.isNaN(rawValue)
    ) {
      return null;
    }
    const pct = (rawValue / shareTotal) * 100;
    if (!Number.isFinite(pct)) return null;
    const rounded = Math.round(pct * 10) / 10;
    const text =
      rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
    return `${text}%`;
  }, [rawValue, shareTotal]);

  const classes = cx(
    styles.item,
    {
      [styles[`item--${type}`]]: !!type,
      [styles['is-negative']]: isNegative,
      [styles['is-muted']]: isMuted,
      [styles['is-selected']]: isSelected,
      [styles['has-offset']]: hasOffset,
      [styles['has-data']]: !!clusterAddress,
      [styles['has-raw-value']]: !!sharePercentLabel,
    },
    className,
  );

  const hasPath = (path || []).length > 0;

  if (isBlank) {
    return <div className={`${styles.item} ${styles['is-blank']}`} />;
  }

  return (
    <div className={classes} onClick={onClick}>
      {sharePercentLabel && (
        <div className={styles.rawValue}>{sharePercentLabel}</div>
      )}

      {riskScore && <AnalyzeItemScoreIndicator value={Number(riskScore)} />}
      <div className={styles.body}>
        {title && (
          <div className={styles.title} title={title}>
            {title}
          </div>
        )}
        {description && <div className={styles.description}>{description}</div>}
      </div>

      <div className={styles.info}>
        {value && <div className={styles.value}>{amountSign + value}</div>}
        {hasPath && (
          <div className={`${styles.path} ${styles.clusterAddress}`}>
            <span>{t('translation.shortest_path')}</span>
            <b>ᐅ</b>
          </div>
        )}
        {!hasPath && !!clusterAddress && (
          <div className={styles.clusterAddress}>
            <span>{clusterAddress}</span>
            <b>ᐅ</b>
          </div>
        )}
        {caption && <div className={styles.caption}>{caption}</div>}
      </div>
    </div>
  );
};

export default AnalyzeItem;
